import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, of, ReplaySubject, Subject, Subscription, switchMap } from 'rxjs';
import { RequestParams } from '../../models/requestParams';
import { ITableData } from '../../models/table';
import { ProductCard } from '../../../product/models/product-card';
import { PhSimpleTableComponent } from '../ph-simple-table/ph-simple-table.component';
import { TranslateService } from '@ngx-translate/core';
import { ProductsService } from '../../services/products.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { MixpanelService } from '../../services/mixpanel.service';
import { catchError, finalize } from 'rxjs/operators';
import {
	IProductCard,
	IProductHistoryChild,
	IProductHistoryTableItem,
	ProductChangeTypes
} from '../../../product/models/models';
import { SessionService } from '../../services/session.service';
import { PHPermissions } from '../../models/PHPermissions';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { MatDialog } from '@angular/material/dialog';
import { ProductCardService } from '../../services/product-card.service';
import { DefaultProductParams } from '../../models/defaultProductParams';

import { SHARED_MODULES } from '../../../shared.';
import { MeasurementUnitPipe } from '../../pipes/measurement-unit.pipe';
import { ProductTypeTitlePipe } from '../../pipes/product-type-title.pipe';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { UnitConverterPipe } from '../../pipes/unit-converter.pipe';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';

@Component({
    selector: 'app-product-change-history-table[product]',
    templateUrl: './product-change-history-table.component.html',
    styleUrls: ['./product-change-history-table.component.scss'],
	imports: [
		SHARED_MODULES,
		MeasurementUnitPipe,
		ProductTypeTitlePipe,
		SanitizerPipe,
		UnitConverterPipe,
		PhButtonComponent,
		PhIconComponent,
		PhLoaderComponent,
		PhSimpleTableComponent
	],
    standalone: true
})
export class ProductChangeHistoryTableComponent implements OnInit, OnDestroy {

	private _loadData$: ReplaySubject<void> = new ReplaySubject<void>(1);
	private _requestParams: RequestParams;
	private _subscriptions: Array<Subscription> = [];

	public changeTypes: typeof ProductChangeTypes = ProductChangeTypes;
	public defaultProductParams: DefaultProductParams = new DefaultProductParams();
	public paramsEditAccess: boolean = false;
	public photoEditAccess: boolean = false;
	public showLoader: boolean = false;
	public sorting$: Subject<string> = new Subject<string>();
	public tableData: ITableData<IProductHistoryTableItem>;
	public update$: Subject<void> = new Subject<void>();

	@Input() product: ProductCard;
	@Input() savingSubscription: Subscription;

	@Output('revert') revertEmitter: EventEmitter<ProductChangeTypes> = new EventEmitter<ProductChangeTypes>();

	@ViewChild(PhSimpleTableComponent) private _tableComponent: PhSimpleTableComponent;

	constructor(
		private dialog: MatDialog,
		private translate: TranslateService,
		private sessionService: SessionService,
		private productsService: ProductsService,
		private productCardService: ProductCardService,
		private snackBarService: SnackBarService,
		private mixPanelService: MixpanelService
	) {
		this.paramsEditAccess = this.sessionService.hasAccess(PHPermissions.ProductParametersEdit);
		this.photoEditAccess = this.sessionService.hasAccess(PHPermissions.ProductImageEdit);
	}

	ngOnInit(): void {
		this._requestParams = new RequestParams({
			page: 1,
			page_size: 30
		});

		this._subscriptions.push(
			this._loadData$
				.pipe(
					switchMap(() => {
						return this.productsService
							.getProductHistory(this.product.id, this._requestParams.getParams())
							.pipe(
								finalize(() => this.showLoader = false),
								catchError(() => {
									this.snackBarService.setErrorNotification();
									return of({
										has_data: false,
										next: null,
										count: 0,
										results: {
											columns: [],
											dataSource: []
										}
									});
								})
							);
					})
				)
				.subscribe((response: ITableData<IProductHistoryTableItem>) => {
					this.product.initHistory(response);
					this.tableData = this.product.history.data;
				}),

			this.sorting$.subscribe((value: string) => {
				this._requestParams.updateParams({ ordering: value !== 'default' ? value : '' });
				this.loadData();
			}),

			this.update$
				.pipe(
					switchMap(() => {
						this._requestParams.updateParams({
							page: this._requestParams.getParam('page') + 1
						});
						return this.productsService.getProductHistory(this.product.id, this._requestParams.getParams());
					})
				)
				.subscribe((response: ITableData<any>) => {
					this.product.updateHistory(response);
					this.tableData = this.product.history.data;
				})
		);

		if (this.product.loadedHistory) {
			this.tableData = this.product.history.data;
		} else {
			this.showLoader = true;
			this.loadData();
		}
	}

	public loadChildren$(key: number): Observable<IProductHistoryChild> {
		return this.productsService.getProductHistoryChildren(key, this.product.id);
	}

	public loadData(): void {
		this._requestParams.updateParams({ page: 1 });
		if (this._tableComponent) {
			this._tableComponent.startLoading();
			this._tableComponent.scrollToTop();
			this._requestParams.updateParams({ ids: this._tableComponent.expandedChildren });
		}
		this._loadData$.next();
	}

	public revertParams(id: number, type: ProductChangeTypes): void {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '700px',
			height: 'auto',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'CHANGE_PRODUCT_INFO',
				twoAnswers: true,
				confirmLabel: 'COMMON.CONFIRM',
				cancelLabel: 'COMMON.CANCEL'
			}
		});

		dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
			if (!result.answer) return;

			this.showLoader = true;
			this.savingSubscription = this.productsService.revertParams(id, this.product.id)
				.pipe(finalize(() => this.showLoader = false))
				.subscribe({
                    next: (product: IProductCard) => {
                        this.loadData();
						this.revertEmitter.emit(type);
                        this.productCardService.saveSubject.next(product);
                        this.mixPanelService.trackPage(MixpanelEvents.ProductsParamsRevert, {
                            article: this.product.loadedParams ? this.product.params.article : 'Unknown',
                            barCode: this.product.loadedParams ? this.product.params.barcode : 'Unknown'
                        });
                    },
                    error: () => this.snackBarService.setErrorNotification()
                })
		});
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}
